import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import { VILLAGE_BUILDINGS, type VillageBuildingId } from '../village/buildings';
import { mountShell, paintShellBackdrop } from '../ui/shellUi';
import { createSaveStore, dungeonFromSave } from '../../persistence/saveStore';
import { addKitNineSlice, KIT_STATES } from '../ui/kit';
import { addGameIcon, TOWN_BUILDING_ICONS } from '../ui/icons';
import { addPixelText } from '../ui/pixelText';
import { UI } from '../ui/theme';

/**
 * Town (Village) — kit-framed building cards + Phosphor building icons.
 */
export class VillageScene extends Phaser.Scene {
  constructor() {
    super('Village');
  }

  create(): void {
    paintShellBackdrop(this, 0x121820);
    const bounds = mountShell(this, 'town');
    const cx = GAME_WIDTH / 2;

    const path = this.add.graphics();
    path.lineStyle(8, UI.townPath, 0.55);
    path.beginPath();
    path.moveTo(cx, bounds.top + 16);
    path.lineTo(cx, 200);
    path.lineTo(108, 360);
    path.moveTo(cx, 200);
    path.lineTo(282, 360);
    path.moveTo(108, 360);
    path.lineTo(108, 520);
    path.moveTo(282, 360);
    path.lineTo(282, 520);
    path.strokePath();
    path.lineStyle(3, UI.goldDim, 0.25);
    path.beginPath();
    path.moveTo(cx, bounds.top + 16);
    path.lineTo(cx, 200);
    path.strokePath();

    const store = createSaveStore();
    const save = store.load();
    const dungeon = dungeonFromSave(save);
    const activeRun =
      save?.run &&
      save.run.status !== 'settled' &&
      dungeon &&
      dungeon.outcome === 'ongoing';

    if (activeRun && dungeon) {
      addPixelText(
        this,
        cx,
        bounds.top + 4,
        `Run in progress - ${dungeon.dungeonName} ${dungeon.encounterIndex + 1}/${dungeon.encounterCount}`,
        { font: 'ui-yellow', size: 10, originX: 0.5 },
      );
    }

    for (const building of VILLAGE_BUILDINGS) {
      this.spawnBuildingCard(building);
    }
  }

  private spawnBuildingCard(
    building: (typeof VILLAGE_BUILDINGS)[number],
  ): void {
    const w = building.width;
    const h = building.height;
    const x = building.x - w / 2;
    const y = building.y - h / 2;

    const fill = this.add.graphics();
    fill.fillStyle(building.color, 0.92);
    fill.fillRoundedRect(x + 4, y + 4, w - 8, h - 8, 4);

    const frameKey =
      building.status === 'open'
        ? KIT_STATES.panel.ornate
        : KIT_STATES.panel.stone;
    addKitNineSlice(this, frameKey, x, y, w, h);
    addKitNineSlice(
      this,
      KIT_STATES.slot.normal,
      building.x - 22,
      building.y - 36,
      44,
      34,
    );

    const iconId =
      TOWN_BUILDING_ICONS[building.id as VillageBuildingId] ??
      'town.dungeon_gate';
    addGameIcon(this, iconId, building.x, building.y - 19, {
      size: 22,
      color: 0xf0e8d8,
    });

    addPixelText(this, building.x, building.y + 2, building.label, {
      font: 'ui-yellow',
      size: 13,
      originX: 0.5,
    });
    addPixelText(this, building.x, building.y + 22, building.statusLabel, {
      font: building.status === 'coming_soon' ? 'ui-grey' : 'ui-brown',
      size: 12,
      originX: 0.5,
    });

    const hit = this.add
      .rectangle(building.x, building.y, w, h, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });

    hit.on('pointerover', () => {
      fill.clear();
      fill.fillStyle(building.color, 1);
      fill.fillRoundedRect(x + 4, y + 4, w - 8, h - 8, 4);
      fill.lineStyle(2, UI.goldBright, 0.85);
      fill.strokeRoundedRect(x + 6, y + 6, w - 12, h - 12, 3);
    });
    hit.on('pointerout', () => {
      fill.clear();
      fill.fillStyle(building.color, 0.92);
      fill.fillRoundedRect(x + 4, y + 4, w - 8, h - 8, 4);
    });
    hit.on('pointerup', () => {
      if (building.sceneKey === 'ComingSoon') {
        this.scene.start('ComingSoon', {
          buildingId: building.id,
          title: building.label,
        });
      } else {
        this.scene.start(building.sceneKey);
      }
    });
  }
}
